
import React, { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import StarIcon from "@/components/part-card/metrics-row/icons/StarIcon";
import SavePreferencesButton from "@/components/sidebar/filters/SavePreferencesButton";

interface RatingSliderProps {
  minStarRating: number;
  setMinStarRating: (rating: number) => void;
}

const RatingSlider = ({ minStarRating, setMinStarRating }: RatingSliderProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const saveRatingPreference = async () => {
    if (!user) {
      toast({
        title: "Not logged in",
        description: "You need to be logged in to save preferences",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert({ 
          id: user.id,
          min_rating: minStarRating 
        });

      if (error) throw error;
      
      toast({
        title: "Preference saved",
        description: `Minimum rating preference set to ${minStarRating}`,
      });
    } catch (error) {
      console.error("Error saving rating preference:", error);
      toast({
        title: "Error",
        description: "Failed to save your preference",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-border mt-auto p-4">
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">
          <StarIcon rating={minStarRating} starPosition={1} iconSize={16} />
        </div>
        <div className="flex-1">
          <Slider
            defaultValue={[0]}
            value={[minStarRating]}
            onValueChange={(value) => setMinStarRating(value[0])}
            max={6}
            step={1}
          />
        </div>
        <SavePreferencesButton onClick={saveRatingPreference} saving={saving} />
      </div>
    </div>
  );
};

export default RatingSlider;
